import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MdCheck, MdContentCopy, MdQrCode2, MdRefresh } from 'react-icons/md';
import { PASSPHRASE_WORDS } from './data/passphraseWords';
import {
  buildPools,
  countEnabledCharOptions,
  estimateStrength,
  generatePassphrase,
  generateRandomPassword,
} from './utils/passwordUtils';
import { sendEvent } from '../../common/analytics';
import { sendPasswordToQrGenerator } from '../../common/passwordToQrTransfer';

const MotionBox = motion(Box);

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

const PRESETS = {
  quick: {
    memorable: false,
    length: 12,
    wordCount: 5,
    upper: true,
    lower: true,
    numbers: true,
    symbols: true,
    avoidAmbiguous: false,
  },
  strong: {
    memorable: false,
    length: 16,
    wordCount: 5,
    upper: true,
    lower: true,
    numbers: true,
    symbols: true,
    avoidAmbiguous: false,
  },
  maximum: {
    memorable: false,
    length: 24,
    wordCount: 5,
    upper: true,
    lower: true,
    numbers: true,
    symbols: true,
    avoidAmbiguous: false,
  },
  memorable: {
    memorable: true,
    length: 16,
    wordCount: 5,
    upper: true,
    lower: true,
    numbers: true,
    symbols: true,
    avoidAmbiguous: false,
  },
};

const PRESET_GRID = [
  { id: 'quick', label: 'Quick' },
  { id: 'strong', label: 'Strong' },
  { id: 'maximum', label: 'Maximum' },
  { id: 'memorable', label: 'Memorable' },
];

const PG_STORAGE_KEY = 'vorakit_password_generator_v1';

const DEFAULT_PG_SETTINGS = {
  memorable: false,
  length: 16,
  wordCount: 5,
  upper: true,
  lower: true,
  numbers: true,
  symbols: true,
  avoidAmbiguous: false,
};

function sanitizePgSettings(raw) {
  const out = { ...DEFAULT_PG_SETTINGS };
  if (!raw || typeof raw !== 'object') return out;

  if (typeof raw.memorable === 'boolean') out.memorable = raw.memorable;

  let len = Number(raw.length);
  if (!Number.isFinite(len)) len = DEFAULT_PG_SETTINGS.length;
  out.length = Math.min(64, Math.max(8, Math.round(len)));

  let wc = Number(raw.wordCount);
  if (!Number.isFinite(wc)) wc = DEFAULT_PG_SETTINGS.wordCount;
  out.wordCount = Math.min(8, Math.max(3, Math.round(wc)));

  if (typeof raw.upper === 'boolean') out.upper = raw.upper;
  if (typeof raw.lower === 'boolean') out.lower = raw.lower;
  if (typeof raw.numbers === 'boolean') out.numbers = raw.numbers;
  if (typeof raw.symbols === 'boolean') out.symbols = raw.symbols;
  if (typeof raw.avoidAmbiguous === 'boolean') out.avoidAmbiguous = raw.avoidAmbiguous;

  const ec = countEnabledCharOptions({
    upper: out.upper,
    lower: out.lower,
    numbers: out.numbers,
    symbols: out.symbols,
  });
  if (!out.memorable && ec === 0) {
    out.lower = true;
  }

  const minLen = Math.max(8, out.memorable ? 3 : ec || 1);
  if (!out.memorable && out.length < minLen) {
    out.length = minLen;
  }

  return out;
}

function readPgSettingsFromStorage() {
  if (typeof localStorage === 'undefined') {
    return { ...DEFAULT_PG_SETTINGS };
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(PG_STORAGE_KEY) || 'null');
    return sanitizePgSettings(parsed);
  } catch {
    return { ...DEFAULT_PG_SETTINGS };
  }
}

function matchesPreset(state, key) {
  const p = PRESETS[key];
  if (p.memorable !== state.memorable) return false;
  if (state.memorable) return state.wordCount === p.wordCount;
  return (
    state.length === p.length &&
    state.upper === p.upper &&
    state.lower === p.lower &&
    state.numbers === p.numbers &&
    state.symbols === p.symbols &&
    state.avoidAmbiguous === p.avoidAmbiguous
  );
}

function derivePresetKey(state) {
  const order = ['quick', 'strong', 'maximum', 'memorable'];
  for (const k of order) {
    if (matchesPreset(state, k)) return k;
  }
  return null;
}

function CharChip({ label, active, onToggle, isDisabled }) {
  return (
    <Button
      type="button"
      size="xs"
      variant={active ? 'solid' : 'outline'}
      colorScheme="teal"
      borderRadius="md"
      px={2}
      minW="2.35rem"
      h="28px"
      fontWeight="700"
      fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
      fontSize="11px"
      aria-pressed={active}
      isDisabled={isDisabled}
      onClick={() => onToggle(!active)}
    >
      {label}
    </Button>
  );
}

const PasswordGenerator = () => {
  const navigate = useNavigate();
  const initial = useMemo(() => readPgSettingsFromStorage(), []);
  const [memorable, setMemorable] = useState(initial.memorable);
  const [length, setLength] = useState(initial.length);
  const [wordCount, setWordCount] = useState(initial.wordCount);
  const [upper, setUpper] = useState(initial.upper);
  const [lower, setLower] = useState(initial.lower);
  const [numbers, setNumbers] = useState(initial.numbers);
  const [symbols, setSymbols] = useState(initial.symbols);
  const [avoidAmbiguous, setAvoidAmbiguous] = useState(initial.avoidAmbiguous);
  const [regenTick, setRegenTick] = useState(0);
  const [copied, setCopied] = useState(false);
  const [countInputFocused, setCountInputFocused] = useState(false);
  const [countDraft, setCountDraft] = useState('');
  const copyTimerRef = useRef(null);

  const enabledCount = countEnabledCharOptions({ upper, lower, numbers, symbols });
  const minLength = Math.max(8, memorable ? 3 : enabledCount || 1);

  useEffect(() => {
    if (!memorable && enabledCount === 0) {
      setLower(true);
    }
  }, [memorable, enabledCount]);

  useEffect(() => {
    if (!memorable && length < minLength) {
      setLength(minLength);
    }
  }, [memorable, length, minLength]);

  const password = useMemo(() => {
    void regenTick;
    if (memorable) {
      return generatePassphrase(wordCount, PASSPHRASE_WORDS);
    }
    const opts = { upper, lower, numbers, symbols, avoidAmbiguous };
    const p = buildPools(opts);
    if (!p.length) return '';
    return generateRandomPassword(length, opts);
  }, [
    memorable,
    wordCount,
    length,
    upper,
    lower,
    numbers,
    symbols,
    avoidAmbiguous,
    regenTick,
  ]);

  const strength = useMemo(() => {
    const pools = buildPools({ upper, lower, numbers, symbols, avoidAmbiguous });
    return estimateStrength({
      memorable,
      password,
      pools,
      wordListLength: PASSPHRASE_WORDS.length,
      wordCount: memorable ? wordCount : 0,
    });
  }, [memorable, password, upper, lower, numbers, symbols, avoidAmbiguous, wordCount]);

  const presetKey = derivePresetKey({
    memorable,
    length,
    wordCount,
    upper,
    lower,
    numbers,
    symbols,
    avoidAmbiguous,
  });

  const applyPreset = (key) => {
    const p = PRESETS[key];
    setMemorable(p.memorable);
    setLength(p.length);
    setWordCount(p.wordCount);
    setUpper(p.upper);
    setLower(p.lower);
    setNumbers(p.numbers);
    setSymbols(p.symbols);
    setAvoidAmbiguous(p.avoidAmbiguous);
    setRegenTick((t) => t + 1);
    sendEvent('PasswordGenerator', 'Preset', key);
  };

  const regenerate = useCallback(() => {
    setRegenTick((t) => t + 1);
    sendEvent('PasswordGenerator', 'Regenerate', 'click');
  }, []);

  const onSendToQr = useCallback(() => {
    if (!password) return;
    sendPasswordToQrGenerator(password);
    sendEvent('PasswordGenerator', 'SendToQr', 'click');
    navigate('/qrcodegenerator');
  }, [password, navigate]);

  const toggleChar = (which, value) => {
    if (!value && enabledCount <= 1 && !memorable) {
      return;
    }
    if (which === 'upper') setUpper(value);
    if (which === 'lower') setLower(value);
    if (which === 'numbers') setNumbers(value);
    if (which === 'symbols') setSymbols(value);
  };

  const onCopy = useCallback(async () => {
    if (!password) return;
    const ok = await copyToClipboard(password);
    if (ok) {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      setCopied(true);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
      sendEvent('PasswordGenerator', 'Copy', 'password');
    }
  }, [password]);

  useEffect(() => () => copyTimerRef.current && clearTimeout(copyTimerRef.current), []);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(
        PG_STORAGE_KEY,
        JSON.stringify({
          memorable,
          length,
          wordCount,
          upper,
          lower,
          numbers,
          symbols,
          avoidAmbiguous,
        })
      );
    } catch (_) {
      /* quota / private mode */
    }
  }, [memorable, length, wordCount, upper, lower, numbers, symbols, avoidAmbiguous]);

  const sliderValue = memorable ? wordCount : length;
  const sliderMax = memorable ? 8 : 64;
  const sliderMin = memorable ? 3 : minLength;

  const onSliderChange = (v) => {
    if (memorable) setWordCount(v);
    else setLength(v);
  };

  useEffect(() => {
    setCountInputFocused(false);
  }, [memorable]);

  const strengthBg = [
    'linear-gradient(90deg, #fecaca, #fca5a5)',
    'linear-gradient(90deg, #fde68a, #fcd34d)',
    'linear-gradient(90deg, #a7f3d0, #5eead4)',
    'linear-gradient(90deg, #6ee7b7, #14b8a6)',
  ][strength.level];

  const commitCountFromDraft = () => {
    let n = parseInt(countDraft, 10);
    if (Number.isNaN(n)) {
      n = sliderValue;
    }
    const clamped = Math.min(sliderMax, Math.max(sliderMin, n));
    onSliderChange(clamped);
    setCountDraft(String(clamped));
  };

  const lengthBlock = (
    <Box>
      <Flex align="center" justify="space-between" gap={3} mb={1} flexWrap="wrap">
        <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.06em">
          {memorable ? 'Words' : 'Length'}
        </Text>
        <HStack spacing={1.5} align="center">
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            size="sm"
            w="3.25rem"
            h="32px"
            textAlign="center"
            fontWeight="800"
            fontSize="sm"
            fontVariantNumeric="tabular-nums"
            borderRadius="md"
            borderColor="gray.200"
            px={1}
            value={countInputFocused ? countDraft : String(sliderValue)}
            aria-label={memorable ? 'Number of words (type or use slider)' : 'Password length in characters (type or use slider)'}
            onFocus={() => {
              setCountInputFocused(true);
              setCountDraft(String(sliderValue));
            }}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '');
              setCountDraft(digits);
              const n = parseInt(digits, 10);
              if (!Number.isNaN(n) && n >= sliderMin && n <= sliderMax) {
                onSliderChange(n);
              }
            }}
            onBlur={() => {
              commitCountFromDraft();
              setCountInputFocused(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
          />
          <Text fontSize="xs" fontWeight="600" color="gray.500" whiteSpace="nowrap">
            {memorable ? 'words' : 'chars'}
          </Text>
        </HStack>
      </Flex>
      <Box
        py={1.5}
        px={0.5}
        mx={-0.5}
        borderRadius="lg"
        onWheel={(e) => {
          if (e.ctrlKey) return;
          e.preventDefault();
          const step = e.deltaY < 0 ? 1 : -1;
          const next = Math.min(sliderMax, Math.max(sliderMin, sliderValue + step));
          if (next !== sliderValue) onSliderChange(next);
        }}
        sx={{ touchAction: 'none' }}
      >
        <Slider
          aria-label={
            memorable
              ? 'Number of words — drag the handle or scroll while hovering'
              : 'Password length in characters — drag the handle or scroll while hovering'
          }
          value={sliderValue}
          min={sliderMin}
          max={sliderMax}
          step={1}
          onChange={onSliderChange}
          focusThumbOnChange={false}
          colorScheme="teal"
        >
          <SliderTrack
            h="10px"
            borderRadius="full"
            bg="gray.200"
            boxShadow="inset 0 1px 3px rgba(0,0,0,0.08)"
          >
            <SliderFilledTrack
              borderRadius="full"
              bg="teal.500"
              boxShadow="inset 0 -1px 0 rgba(0,0,0,0.06)"
            />
          </SliderTrack>
          <SliderThumb
            boxSize="22px"
            bg="teal.500"
            borderWidth="3px"
            borderColor="white"
            boxShadow="0 2px 10px rgba(15, 118, 110, 0.5), 0 1px 3px rgba(0,0,0,0.15)"
            _hover={{ bg: 'teal.600' }}
            _focusVisible={{
              boxShadow:
                '0 2px 10px rgba(15, 118, 110, 0.5), 0 1px 3px rgba(0,0,0,0.15), 0 0 0 3px rgba(45, 212, 191, 0.45)',
            }}
          />
        </Slider>
      </Box>
    </Box>
  );

  const charChips = !memorable && (
    <Box mt={2}>
      <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.06em" mb={1.5}>
        Character sets
      </Text>
      <Wrap spacing={1.5}>
        <WrapItem>
          <CharChip
            label="A-Z"
            active={upper}
            isDisabled={upper && enabledCount <= 1}
            onToggle={(v) => toggleChar('upper', v)}
          />
        </WrapItem>
        <WrapItem>
          <CharChip
            label="a-z"
            active={lower}
            isDisabled={lower && enabledCount <= 1}
            onToggle={(v) => toggleChar('lower', v)}
          />
        </WrapItem>
        <WrapItem>
          <CharChip
            label="0-9"
            active={numbers}
            isDisabled={numbers && enabledCount <= 1}
            onToggle={(v) => toggleChar('numbers', v)}
          />
        </WrapItem>
        <WrapItem>
          <CharChip
            label="#!"
            active={symbols}
            isDisabled={symbols && enabledCount <= 1}
            onToggle={(v) => toggleChar('symbols', v)}
          />
        </WrapItem>
      </Wrap>
    </Box>
  );

  const presetGrid = (
    <Box>
      <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.06em" mb={1.5}>
        Presets
      </Text>
      <SimpleGrid columns={2} spacing={1.5}>
        {PRESET_GRID.map(({ id, label }) => {
          const selected = presetKey === id;
          return (
            <Button
              key={id}
              type="button"
              h="34px"
              minH="34px"
              py={0}
              fontWeight="600"
              fontSize="xs"
              borderRadius="full"
              variant={selected ? 'solid' : 'outline'}
              colorScheme="teal"
              borderWidth="1px"
              borderColor={selected ? 'teal.500' : 'gray.200'}
              bg={selected ? undefined : 'gray.50'}
              color={selected ? undefined : 'gray.700'}
              boxShadow="none"
              _hover={{
                borderColor: selected ? 'teal.400' : 'gray.300',
                bg: selected ? undefined : 'gray.100',
              }}
              onClick={() => applyPreset(id)}
            >
              {label}
            </Button>
          );
        })}
      </SimpleGrid>
    </Box>
  );

  const extraOptions = (
    <HStack mt={2} spacing={1.5} flexWrap="wrap" rowGap={1.5}>
      <Button
        type="button"
        size="xs"
        h="28px"
        borderRadius="full"
        fontWeight="600"
        fontSize="11px"
        variant={memorable ? 'solid' : 'outline'}
        colorScheme="teal"
        borderWidth="1px"
        borderColor={memorable ? 'teal.500' : 'gray.200'}
        bg={memorable ? undefined : 'gray.50'}
        color={memorable ? undefined : 'gray.700'}
        px={2.5}
        aria-pressed={memorable}
        onClick={() => setMemorable((m) => !m)}
      >
        Memorable
      </Button>
      {!memorable && (
        <Button
          type="button"
          size="xs"
          h="28px"
          borderRadius="full"
          fontWeight="600"
          fontSize="11px"
          variant={avoidAmbiguous ? 'solid' : 'outline'}
          colorScheme="teal"
          borderWidth="1px"
          borderColor={avoidAmbiguous ? 'teal.500' : 'gray.200'}
          bg={avoidAmbiguous ? undefined : 'gray.50'}
          color={avoidAmbiguous ? undefined : 'gray.700'}
          px={2.5}
          aria-pressed={avoidAmbiguous}
          onClick={() => setAvoidAmbiguous((a) => !a)}
        >
          Avoid ambiguous
        </Button>
      )}
    </HStack>
  );

  const columnsBlock = (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 5 }} alignItems="start">
      <Box>
        {lengthBlock}
        {charChips}
      </Box>
      <Box>
        {presetGrid}
        {extraOptions}
      </Box>
    </SimpleGrid>
  );

  const mobileAccordion = (
    <Accordion allowToggle defaultIndex={[0]} display={{ base: 'block', md: 'none' }}>
      <AccordionItem border="none">
        <AccordionButton px={0} py={1.5} borderRadius="md" _hover={{ bg: 'gray.50' }}>
          <Text flex="1" textAlign="left" fontSize="sm" fontWeight="700" color="gray.700">
            Length &amp; characters
          </Text>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px={0} pt={0} pb={0.5}>
          {lengthBlock}
          {charChips}
        </AccordionPanel>
      </AccordionItem>
      <AccordionItem border="none">
        <AccordionButton px={0} py={1.5} borderRadius="md" _hover={{ bg: 'gray.50' }}>
          <Text flex="1" textAlign="left" fontSize="sm" fontWeight="700" color="gray.700">
            Presets &amp; options
          </Text>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px={0} pt={0} pb={0.5}>
          {presetGrid}
          {extraOptions}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );

  return (
    <Box
      maxW="920px"
      mx="auto"
      pb={{ base: 8, md: 6 }}
      px={{ base: 0, sm: 1 }}
    >
      <Flex
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Box minW={0}>
          <Heading as="h1" size="md" fontWeight="800" letterSpacing="-0.03em" color="gray.800" lineHeight="1.2">
            Password Generator
          </Heading>
          <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="short" display={{ base: 'none', sm: 'block' }}>
            Strong passwords — fully client-side.
          </Text>
        </Box>
        <Badge
          colorScheme="teal"
          variant="subtle"
          borderRadius="full"
          px={2.5}
          py={0.5}
          fontSize="10px"
          fontWeight="700"
          whiteSpace="nowrap"
        >
          LOCAL ONLY
        </Badge>
      </Flex>

      <Box
        borderRadius="2xl"
        borderWidth="1px"
        borderColor="gray.100"
        bg="linear-gradient(180deg, #ffffff 0%, #fafafa 100%)"
        boxShadow="0 4px 24px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)"
        p={{ base: 3, md: 3.5 }}
        overflow="hidden"
      >
        <Flex align={{ base: 'flex-start', sm: 'stretch' }} gap={2} mb={2} flexWrap="wrap">
          <Box
            as="button"
            type="button"
            flex="1"
            minW={{ base: '100%', sm: 0 }}
            display="flex"
            alignItems="center"
            textAlign="left"
            px={{ base: 4, md: 5 }}
            py={{ base: 3.5, md: 4 }}
            borderRadius="xl"
            bg="gray.100"
            borderWidth="0"
            boxShadow="inset 0 1px 0 rgba(255,255,255,0.65)"
            cursor="pointer"
            transition="background 0.15s ease, box-shadow 0.15s ease"
            _hover={{ bg: 'gray.200' }}
            _active={{ bg: 'gray.200' }}
            onClick={onCopy}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCopy();
              }
            }}
            aria-label="Copy password to clipboard"
          >
            <Box flex="1" minW={0}>
              <AnimatePresence mode="wait">
                <MotionBox
                  key={`${password}-${regenTick}`}
                  initial={{ opacity: 0.35, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0.15, y: -3 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  w="100%"
                >
                  <Text
                    as="span"
                    role="status"
                    aria-live="polite"
                    fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                    fontSize={{ base: 'sm', md: 'md' }}
                    fontWeight="700"
                    color="gray.900"
                    lineHeight="1.45"
                    wordBreak="break-all"
                    userSelect="all"
                    noOfLines={{ base: 4, md: 3 }}
                  >
                    {password}
                  </Text>
                </MotionBox>
              </AnimatePresence>
            </Box>
          </Box>
          <HStack
            spacing={1.5}
            flexShrink={0}
            align="stretch"
            w={{ base: '100%', sm: 'auto' }}
            justify={{ base: 'flex-end', sm: 'flex-start' }}
            alignSelf={{ base: 'auto', sm: 'stretch' }}
          >
            <Button
              type="button"
              size="sm"
              h={{ base: 'auto', sm: '100%' }}
              minH={{ base: '44px', sm: '100%' }}
              py={2}
              px={3}
              leftIcon={<Icon as={MdRefresh} boxSize={4} />}
              variant="outline"
              colorScheme="gray"
              borderRadius="lg"
              fontWeight="700"
              borderColor="gray.200"
              color="gray.600"
              _hover={{ bg: 'gray.50', color: 'gray.800', borderColor: 'gray.300' }}
              onClick={(e) => {
                e.stopPropagation();
                regenerate();
              }}
              aria-label="Generate a new password"
            >
              Refresh
            </Button>
            <Button
              type="button"
              size="sm"
              h={{ base: 'auto', sm: '100%' }}
              minH={{ base: '44px', sm: '100%' }}
              py={2}
              px={3}
              leftIcon={<Icon as={copied ? MdCheck : MdContentCopy} boxSize={4} />}
              variant={copied ? 'solid' : 'outline'}
              colorScheme="teal"
              borderRadius="lg"
              fontWeight="700"
              borderColor={copied ? undefined : 'gray.200'}
              onClick={(e) => {
                e.stopPropagation();
                onCopy();
              }}
              aria-label="Copy password to clipboard"
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button
              type="button"
              size="sm"
              h={{ base: 'auto', sm: '100%' }}
              minH={{ base: '44px', sm: '100%' }}
              py={2}
              px={3}
              leftIcon={<Icon as={MdQrCode2} boxSize={4} />}
              variant="outline"
              colorScheme="blue"
              borderRadius="lg"
              fontWeight="700"
              borderColor="blue.200"
              color="blue.700"
              isDisabled={!password}
              _hover={{ bg: 'blue.50', borderColor: 'blue.300' }}
              onClick={(e) => {
                e.stopPropagation();
                onSendToQr();
              }}
              aria-label="Open QR Code generator with this password as text"
            >
              QR Code
            </Button>
          </HStack>
        </Flex>

        <Flex align="center" gap={2.5} mb={2}>
          <Box flex="1" minW={0} h="4px" borderRadius="full" bg="gray.200" overflow="hidden">
            <MotionBox
              h="100%"
              borderRadius="full"
              style={{ background: strengthBg }}
              initial={false}
              animate={{ width: `${strength.fill}%` }}
              transition={{ type: 'spring', stiffness: 420, damping: 38 }}
            />
          </Box>
          <Text fontSize="xs" fontWeight="700" color="gray.700" flexShrink={0}>
            {strength.label}
          </Text>
        </Flex>

        <Box display={{ base: 'none', md: 'block' }}>{columnsBlock}</Box>
        {mobileAccordion}
      </Box>
    </Box>
  );
};

export default PasswordGenerator;
